import { Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TreeNode } from '../dataDisplay/tree/types';

interface TreeFilterOptions<TItem> {
  /** Whether to show search functionality */
  showSearch?: boolean;
  /** Custom filter function to determine if an item matches the search */
  filterPredicate?: (item: TItem, searchString: string) => boolean;
  /** Custom function to convert filtered items to TreeNode format */
  itemToTreeNode?: (item: TItem) => TreeNode;
}

interface TreeFilterResult<TItem> {
  /** The filtered tree data */
  filteredData: TreeNode[];
  /** Current search string value */
  searchString: string;
  /** Props to pass to the search input component */
  searchProps: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  };
  /** Function to handle keyboard navigation from search to tree */
  handleKeyNavigation: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Custom hook for filtering tree data with search functionality
 * @param originalData - The original tree data to display when not searching
 * @param sourceItems - The source items to filter when searching (can be flat array)
 * @param options - Configuration options for filtering behavior
 * @returns Filtered data and search-related props
 */
export function useTreeFilter<TItem>(
  originalData: TreeNode[],
  sourceItems: TItem[] | null,
  options: TreeFilterOptions<TItem> = {}
): TreeFilterResult<TItem> {
  const { showSearch = true, filterPredicate, itemToTreeNode } = options;

  const { t } = useTranslation();
  const [searchString, setSearchString] = useState('');

  // Handle keyboard navigation from search input to tree
  const handleKeyNavigation = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.code === 'ArrowDown') {
        setTimeout(() => {
          if (e?.target) {
            const htmlTarget = e.target as HTMLElement;
            htmlTarget.blur();
          }
          // Try to focus the first tree item
          const treeView = document.querySelector('[role="tree"]');
          if (treeView) {
            const firstTreeItem = treeView.querySelector(
              '[role="treeitem"]'
            ) as HTMLElement;
            if (firstTreeItem) {
              firstTreeItem.focus();
            }
          }
        });
      }
    },
    []
  );

  // Filter the data based on search string
  const filteredData = useMemo<TreeNode[]>(() => {
    // Return original tree if no search or search is empty
    if (!showSearch || searchString.trim() === '') {
      return originalData;
    }

    if (!sourceItems) {
      return [];
    }

    // Use custom filter predicate if provided, otherwise use default
    const predicate =
      filterPredicate ||
      ((item: any) => {
        // Default behavior: try to filter by 'name' property
        return item.name?.toLowerCase().includes(searchString.toLowerCase());
      });

    const matchingItems = sourceItems.filter((item) =>
      predicate(item, searchString)
    );

    // Use custom converter if provided, otherwise use default
    const converter =
      itemToTreeNode ||
      ((item: any) => ({
        id: String(item.id || item.key || Math.random()),
        label: (
          <Typography component="div">
            <span>{item.name || String(item)}</span>
          </Typography>
        ),
        children: undefined, // Flat list when searching
      }));

    return matchingItems.map(converter);
  }, [
    originalData,
    sourceItems,
    showSearch,
    searchString,
    filterPredicate,
    itemToTreeNode,
  ]);

  const searchProps = {
    value: searchString,
    onChange: setSearchString,
    placeholder: t('PICKER.SEARCH.DEFAULT'),
    onKeyDown: handleKeyNavigation,
  };

  return {
    filteredData,
    searchString,
    searchProps,
    handleKeyNavigation,
  };
}
